/**
 * HomepageConversionEngine.jsx — Try Granullo
 *
 * FOUNDER DIRECTIVE: SYSTEM EXPRESSION, NOT COMPONENT
 *
 * This is the primary interface to the Granullo Intelligence Layer.
 * Chips, input, voice ALL feed into the SAME system state.
 * Transaction Simulator uses the SAME condition engine.
 * NO duplication of logic — everything flows through the brain.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock, ShieldCheck, Check, X, Loader2, ArrowRight,
  AlertCircle, Mic, Zap, Users, Truck, Landmark,
  Shield, GraduationCap, Heart, Rocket, Leaf, Target,
} from 'lucide-react';
import { createProgram, submitAssistedSetup, buildProgramPayload } from '../../lib/programApi';
import { lensAnnotation, LENS_TYPES } from '../lens-v2/LensAnnotations';
import {
  useGranulloIntelligence,
  CHIPS, COLORS as C, inferBeneficiary, generateChecks,
} from '../../contexts/GranulloIntelligenceContext';

const ICON_MAP = { Users, Truck, GraduationCap, Heart, Rocket, Leaf, Shield, Landmark, Target };

const ROTATING_PLACEHOLDERS = [
  '$200 employee wellness benefit, gym only',
  'Subsidy for seeds, verified suppliers only',
  'Vendor payment after delivery confirmation',
  'Insurance payout for home repairs only',
  'Grant funds after milestone evidence',
];

const NODES = Array.from({ length: 8 }, (_, i) => ({
  x: 12 + (i % 4) * 22 + (Math.sin(i * 1.3) * 5),
  y: 18 + Math.floor(i / 4) * 32 + (Math.cos(i * 1.7) * 5),
  dx: Math.sin(i * 2.1) * 16,
  dy: Math.cos(i * 1.5) * 12,
  speed: 3.5 + (i % 3) * 1.2,
}));

const AWAKENING_SCENARIO = {
  title: 'Vendor payment after delivery confirmation',
  fields: [
    { label: 'WHO', value: 'Verified suppliers only' },
    { label: 'CONDITIONS', value: 'Delivery receipt + quality check' },
    { label: 'WHERE', value: 'Registered agricultural vendors' },
    { label: 'AMOUNT', value: 'Up to $45,000 per order' },
  ],
  tags: ['identity', 'purpose', 'amount', 'delivery'],
};

function AwakeningPanel({ intensity, systemStatus, onInject }) {
  const [revealStep, setRevealStep] = useState(0);
  const [loopKey, setLoopKey] = useState(0);

  useEffect(() => {
    if (intensity > 0.1) return;
    setRevealStep(0);
    const timers = [
      setTimeout(() => setRevealStep(1), 400),
      setTimeout(() => setRevealStep(2), 900),
      setTimeout(() => setRevealStep(3), 1400),
      setTimeout(() => setRevealStep(4), 1900),
      setTimeout(() => setRevealStep(5), 2400),
      setTimeout(() => setRevealStep(6), 3200),
      setTimeout(() => { setRevealStep(0); setLoopKey(k => k + 1); }, 12000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [loopKey, intensity]);

  return (
    <motion.div key="awakening" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
      className="relative min-h-[380px] flex flex-col items-center justify-center overflow-hidden" data-testid="system-awakening">
      {/* Background grid */}
      <div className="absolute inset-0 awakening-bg" style={{ opacity: 0.04 }} />

      {/* Connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        {NODES.slice(0, 6).map((n, i) => {
          const next = NODES[(i + 1) % NODES.length];
          return (
            <motion.line key={`l-${i}`} x1={`${n.x}%`} y1={`${n.y}%`} x2={`${next.x}%`} y2={`${next.y}%`}
              stroke="#B8D7F0" strokeWidth="0.5"
              initial={{ opacity: 0, pathLength: 0 }}
              animate={{ opacity: revealStep >= 2 ? [0.06, 0.2, 0.06] : 0, pathLength: revealStep >= 2 ? 1 : 0 }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.3 }} />
          );
        })}
      </svg>

      {/* Ambient nodes */}
      {NODES.map((n, i) => (
        <motion.div key={i} className="absolute rounded-full"
          style={{ left: `${n.x}%`, top: `${n.y}%`, width: 3, height: 3, background: `#B8D7F0` }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: revealStep >= 2 ? [0.08, 0.25, 0.08] : 0,
            x: [0, n.dx * 0.5, 0], y: [0, n.dy * 0.5, 0],
          }}
          transition={{ duration: n.speed, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }} />
      ))}

      {/* Central focal node — Granullo awakening */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div className="rounded-full"
          style={{ width: 120, height: 120, background: 'radial-gradient(circle, rgba(184,215,240,0.06) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} />
      </div>

      <AnimatePresence mode="wait">
        {intensity < 0.1 ? (
          <motion.div key={`idle-${loopKey}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="relative z-10 w-full max-w-[320px] px-2">

            {/* System label */}
            <motion.p className="text-[9px] font-mono uppercase tracking-[0.14em] text-center mb-5"
              initial={{ opacity: 0 }} animate={{ opacity: revealStep >= 1 ? 0.35 : 0 }}
              style={{ color: '#B8D7F0' }} data-testid="awakening-label">
              System awakening
            </motion.p>

            {/* Central node */}
            <motion.div className="w-3 h-3 mx-auto mb-5 rounded-full"
              style={{ background: '#B8D7F0' }}
              animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0.8, 0.3], boxShadow: ['0 0 0px rgba(184,215,240,0)', '0 0 16px rgba(184,215,240,0.3)', '0 0 0px rgba(184,215,240,0)'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />

            {/* Scenario title */}
            <motion.p className="text-[13px] font-mono text-center mb-5 font-medium"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: revealStep >= 1 ? 0.75 : 0, y: revealStep >= 1 ? 0 : 4 }}
              transition={{ duration: 0.4 }}
              style={{ color: 'rgba(var(--ce-text-rgb),0.75)' }} data-testid="awakening-title">
              {AWAKENING_SCENARIO.title}
            </motion.p>

            {/* Fields — line by line */}
            <div className="space-y-2.5 mb-5">
              {AWAKENING_SCENARIO.fields.map((f, i) => (
                <motion.div key={f.label} className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: revealStep >= i + 2 ? 1 : 0, x: revealStep >= i + 2 ? 0 : -6 }}
                  transition={{ duration: 0.35 }}>
                  <span className="text-[9px] font-mono uppercase tracking-[0.1em] mt-0.5 w-[80px] shrink-0"
                    style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }}>{f.label}</span>
                  <span className="text-[12px]" style={{ color: 'rgba(var(--ce-text-rgb),0.55)' }}>{f.value}</span>
                </motion.div>
              ))}
            </div>

            {/* Condition tags */}
            <motion.div className="flex justify-center gap-3 mb-5"
              initial={{ opacity: 0 }} animate={{ opacity: revealStep >= 6 ? 1 : 0 }} transition={{ duration: 0.3 }}>
              {AWAKENING_SCENARIO.tags.map((tag, i) => (
                <motion.div key={tag} className="flex items-center gap-1"
                  animate={{ opacity: [0.25, 0.55, 0.25] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}>
                  <div className="w-1 h-1 rounded-full" style={{ background: '#B8D7F0' }} />
                  <span className="text-[9px] font-mono" style={{ color: 'rgba(184,215,240,0.5)' }}>{tag}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Prompt */}
            <motion.p className="text-[13px] text-center mb-4"
              initial={{ opacity: 0 }} animate={{ opacity: revealStep >= 6 ? 0.5 : 0 }} transition={{ duration: 0.5 }}
              style={{ color: 'rgba(var(--ce-text-rgb),0.5)' }} data-testid="awakening-prompt">
              What should <span style={{ color: '#B8D7F0', borderBottom: '1px solid rgba(184,215,240,0.3)' }}>this money</span> do?
            </motion.p>

            {/* Entry options */}
            <motion.div className="flex justify-center gap-3"
              initial={{ opacity: 0 }} animate={{ opacity: revealStep >= 6 ? 1 : 0 }} transition={{ duration: 0.3 }}>
              {[
                { text: 'Modify this', action: () => onInject && onInject(AWAKENING_SCENARIO.title, 'awakening') },
                { text: 'Describe your own', action: null },
              ].map((opt, i) => (
                <button key={i}
                  onClick={opt.action}
                  className="text-[10px] font-mono px-3 py-1.5 rounded-md transition-all duration-150"
                  style={{
                    color: 'rgba(var(--ce-text-rgb),0.35)',
                    background: 'rgba(var(--ce-text-rgb),0.02)',
                    border: '1px solid rgba(var(--ce-text-rgb),0.06)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(184,215,240,0.25)'; e.currentTarget.style.color = 'rgba(var(--ce-text-rgb),0.55)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(var(--ce-text-rgb),0.06)'; e.currentTarget.style.color = 'rgba(var(--ce-text-rgb),0.35)'; }}
                  data-testid={`awakening-option-${i}`}>
                  {opt.text}
                </button>
              ))}
            </motion.div>
          </motion.div>
        ) : (
          <motion.div key="active" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="relative z-10 text-center">
            <motion.div className="w-3 h-3 mx-auto mb-3 rounded-full"
              style={{ background: '#B8D7F0' }}
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }} />
            <p className="text-[12px] font-mono" style={{ color: 'rgba(184,215,240,0.8)' }}>{systemStatus}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function HomepageConversionEngine() {
  const {
    programText, parsedProgram, selectedChipIndex, rememberedChip,
    systemStatus, nudge, intensity, merchantTests, previewId, hasConditions,
    injectProgram, evaluate, evaluateAsync, clearChipMemory, resetSystem,
    executionLog, consecutiveBlocked, totalTests, confidence, breakMessage,
  } = useGranulloIntelligence();

  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const textareaRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const recognitionRef = useRef(null);
  const hasVoice = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const [constructionPhase, setConstructionPhase] = useState(0);
  const [simulating, setSimulating] = useState(false);
  const [simChecks, setSimChecks] = useState([]);
  const [simVisibleChecks, setSimVisibleChecks] = useState(0);
  const [simResult, setSimResult] = useState(null);
  const simTimersRef = useRef([]);
  const autoSimulateRef = useRef(false);
  const [resultLocked, setResultLocked] = useState(false);
  const [assertionVisible, setAssertionVisible] = useState(false);
  const [deployPhase, setDeployPhase] = useState('idle');
  const [createdProgram, setCreatedProgram] = useState(null);
  const [deployError, setDeployError] = useState(null);
  const [showAssistedForm, setShowAssistedForm] = useState(false);
  const [assistedForm, setAssistedForm] = useState({ name: '', email: '', institution: '' });
  const [assistedStatus, setAssistedStatus] = useState('idle');

  useEffect(() => {
    if (!parsedProgram) { setConstructionPhase(0); return; }
    setConstructionPhase(0);
    const timers = [
      setTimeout(() => setConstructionPhase(1), 150),
      setTimeout(() => setConstructionPhase(2), 320),
      setTimeout(() => setConstructionPhase(3), 490),
      setTimeout(() => setConstructionPhase(4), 660),
      setTimeout(() => setConstructionPhase(5), 830),
      setTimeout(() => setConstructionPhase(6), 1050),
    ];
    return () => timers.forEach(clearTimeout);
  }, [parsedProgram?.type, parsedProgram?.amount]);

  useEffect(() => {
    const i = setInterval(() => setPlaceholderIdx(p => (p + 1) % ROTATING_PLACEHOLDERS.length), 3500);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR(); r.continuous = false; r.interimResults = true; r.lang = 'en-US';
    r.onresult = (e) => {
      const res = e.results[e.results.length - 1];
      injectProgram(res[0].transcript, 'voice');
      setVoiceError('');
      if (res.isFinal) setListening(false);
    };
    r.onerror = (e) => { setListening(false); setVoiceError(e.error === 'not-allowed' ? 'Microphone access denied' : 'Could not understand'); setTimeout(() => setVoiceError(''), 3000); };
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.abort(); } catch {} };
  }, [injectProgram]);

  useEffect(() => {
    const h = (e) => {
      if (e.detail?.text) {
        injectProgram(e.detail.text, e.detail.source || 'external');
        setDeployPhase('idle'); setCreatedProgram(null); setSimResult(null); setSimulating(false);
        setSimChecks([]); setSimVisibleChecks(0); setResultLocked(false); setAssertionVisible(false);
        autoSimulateRef.current = true;
      }
    };
    window.addEventListener('granullo-prevent', h);
    window.addEventListener('granullo:experience-collapse', h);
    return () => {
      window.removeEventListener('granullo-prevent', h);
      window.removeEventListener('granullo:experience-collapse', h);
    };
  }, [injectProgram]);

  const handleChipClick = useCallback((chip, idx) => {
    injectProgram(chip.text, 'chip', idx);
    setDeployPhase('idle'); setCreatedProgram(null); setDeployError(null);
    setShowAssistedForm(false); setAssistedStatus('idle');
    setSimResult(null); setSimulating(false); setSimChecks([]);
  }, [injectProgram]);

  const handleTextChange = useCallback((e) => {
    injectProgram(e.target.value, 'input');
    setDeployPhase('idle'); setSimResult(null); setSimulating(false);
  }, [injectProgram]);

  const toggleVoice = useCallback(() => {
    if (!hasVoice) return;
    if (listening) recognitionRef.current?.stop();
    else { setVoiceError(''); setListening(true); try { recognitionRef.current?.start(); } catch { setListening(false); } }
  }, [listening, hasVoice]);

  const handleSimulate = useCallback((merchant) => {
    if (!parsedProgram || simulating || resultLocked) return;
    simTimersRef.current.forEach(clearTimeout);
    const timers = [];
    setSimulating(true); setSimResult(null); setSimVisibleChecks(0);
    setResultLocked(false); setAssertionVisible(false);

    // Generate local checks for the animation (visual choreography)
    const localChecks = generateChecks(parsedProgram, merchant);
    setSimChecks(localChecks);

    // Start check animation immediately
    localChecks.forEach((_, i) => {
      timers.push(setTimeout(() => setSimVisibleChecks(i + 1), 400 + i * 380));
    });

    // Fire real backend evaluation in parallel
    const resultDelay = 400 + localChecks.length * 380 + 500;
    const backendPromise = evaluateAsync(merchant);

    timers.push(setTimeout(async () => {
      let finalResult = null;

      try {
        // Wait for backend result (should be fast, < 200ms typical)
        const { result: realResult, real } = await backendPromise;
        if (realResult) {
          finalResult = realResult;
          // If real evaluation returned, update checks to show real failed conditions
          if (real && realResult.checks) {
            setSimChecks(realResult.checks);
            setSimVisibleChecks(realResult.checks.length);
          }
        }
      } catch {
        // Graceful: fall back to synchronous mock
      }

      // Fallback: use local mock evaluation if backend didn't respond
      if (!finalResult) {
        finalResult = evaluate(merchant);
      }

      setSimResult(finalResult);
      setSimulating(false);
      setResultLocked(true);

      // Silence moment: assertion appears after 800ms pause
      timers.push(setTimeout(() => setAssertionVisible(true), 800));
      // Unlock after 1.6s total
      timers.push(setTimeout(() => setResultLocked(false), 1600));
    }, resultDelay));

    simTimersRef.current = timers;
  }, [parsedProgram, simulating, resultLocked, evaluate, evaluateAsync]);

  // Auto-simulate: when triggered from Failure Simulator, auto-run evaluation after construction
  useEffect(() => {
    if (!autoSimulateRef.current || constructionPhase < 6 || !merchantTests.length || simulating || resultLocked) return;
    autoSimulateRef.current = false;
    const failMerchant = merchantTests.find(t => !t.allowed) || merchantTests[0];
    const timer = setTimeout(() => handleSimulate(failMerchant), 400);
    return () => clearTimeout(timer);
  }, [constructionPhase, merchantTests, simulating, resultLocked, handleSimulate]);

  const handleDeploy = useCallback(async () => {
    if (!parsedProgram || deployPhase === 'deploying') return;
    setDeployPhase('deploying'); setDeployError(null);
    try {
      const payload = buildProgramPayload(parsedProgram, programText);
      const result = await createProgram(payload);
      setCreatedProgram(result); setDeployPhase('deployed');
    } catch (err) { setDeployError(err.message); setDeployPhase('error'); }
  }, [parsedProgram, programText, deployPhase]);

  const handleAssistedSubmit = useCallback(async (e) => {
    e.preventDefault(); if (assistedStatus === 'submitting') return; setAssistedStatus('submitting');
    try {
      await submitAssistedSetup({ institution: assistedForm.institution, contact_name: assistedForm.name, contact_email: assistedForm.email, use_case: programText.substring(0, 200), beneficiary_type: parsedProgram?.type || 'General', estimated_amount: parsedProgram ? parseFloat((parsedProgram.amount || '0').replace(/[^0-9.]/g, '')) || null : null, timeline: parsedProgram?.timeline || '' });
      setAssistedStatus('done');
    } catch { setAssistedStatus('error'); }
  }, [assistedForm, programText, parsedProgram, assistedStatus]);

  const handleReset = useCallback(() => {
    resetSystem();
    setDeployPhase('idle'); setCreatedProgram(null); setDeployError(null);
    setShowAssistedForm(false); setAssistedStatus('idle'); setAssistedForm({ name: '', email: '', institution: '' });
    setSimResult(null); setSimulating(false); setSimChecks([]);
  }, [resetSystem]);

  if (deployPhase === 'deployed' && createdProgram) {
    const isE = createdProgram.state === 'ELIGIBLE' || createdProgram.state === 'ACTIVE';
    return (
      <section id="try-granullo" data-testid="conversion-engine" className="g-conversion-engine relative py-14 sm:py-18 px-6" style={{ background: 'var(--ce-bg, #F5F5F5)', minHeight: '680px' }}>
        <div className="max-w-[640px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="deploy-confirmation">
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-2 h-2 rounded-full" style={{ background: isE ? C.allowed : C.blocked }} />
              <span className="text-[11px] font-mono uppercase tracking-[0.15em]" style={{ color: 'rgba(var(--ce-text-rgb),0.4)' }}>Rules deployed</span>
            </div>
            <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${isE ? 'rgba(184,215,240,0.2)' : 'rgba(215,210,234,0.2)'}` }}>
              <div className="px-6 pt-5 pb-4" style={{ background: 'rgba(var(--ce-text-rgb),0.02)' }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-mono font-medium" style={{ color: 'rgba(var(--ce-text-rgb),0.5)' }} data-testid="deploy-program-id">{createdProgram.program_id}</span>
                  <span className="text-[11px] font-mono font-semibold uppercase" style={{ color: isE ? C.allowed : C.blocked }} data-testid="deploy-state">{createdProgram.state}</span>
                </div>
                <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary, #e2e8f0)' }} data-testid="deploy-title">{parsedProgram?.type || 'Program'}</h3>
                <p className="text-[13px] mt-1 font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }}>{parsedProgram?.amount}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <Link to="/live-programs" className="ce-btn-primary" data-testid="deploy-view-live">View live program <ArrowRight size={14} /></Link>
              <button onClick={handleReset} className="ce-btn-ghost" data-testid="deploy-create-another">Create another</button>
            </div>
          </motion.div>
        </div>
        <style>{engineStyles}</style>
      </section>
    );
  }

  return (
    <section id="try-granullo" data-testid="conversion-engine" className="g-conversion-engine relative py-14 sm:py-18 px-6" style={{ background: 'var(--ce-bg, #F5F5F5)', minHeight: '680px' }}
      {...lensAnnotation({
        id: 'conversion-engine',
        type: LENS_TYPES.PROGRAM,
        title: 'Granullo Rule Engine — Set Your Structural Conditions',
        meaning: 'The primary interface to the Granullo Intelligence Layer. Define what money can and cannot do. The system generates structural verification rules in real-time.',
        actions: ['Build a program', 'Test conditions', 'View rule output'],
        relations: ['rule-pack', 'pressure-card', 'granullo-pass'],
        state: 'engine-active',
        priority: 'high',
      })}
    >
      <div className="max-w-[1120px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-10 lg:gap-14" style={{ minHeight: '540px' }}>
          <div>
            <motion.h2 initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" style={{ color: 'var(--text-primary, #0A0A0A)' }} data-testid="engine-title">
              Set Your <span style={{ fontFamily: '"Permanent Marker", cursive', color: 'var(--g-editorial-red, #CD2200)', display: 'inline-block', transform: 'rotate(-0.5deg)' }}>Rules</span>
            </motion.h2>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.05 }} className="mb-6">
              <p className="text-[17px] sm:text-[19px] mb-1" style={{ color: 'rgba(var(--ce-text-rgb),0.7)' }} data-testid="engine-question">
                What should <span className="ce-this-money">this money</span> do?
              </p>
              <p className="text-[13px]" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }}>Try the exact scenario you just saw — or describe your own</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="mb-6">
              <div className="grid grid-cols-3 gap-2" data-testid="chip-grid">
                {CHIPS.map((chip, i) => {
                  const Icon = ICON_MAP[chip.icon];
                  const isS = selectedChipIndex === i;
                  const isR = selectedChipIndex === null && rememberedChip === chip.label;
                  const accent = CHIP_COLORS[chip.label] || '#B8D7F0';
                  return (
                    <motion.button key={chip.label} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      transition={{ delay: 0.12 + i * 0.03 }} onClick={() => handleChipClick(chip, i)}
                      className={`ce-chip group ${isS ? 'selected' : ''} ${isR ? 'remembered' : ''}`}
                      style={{ '--chip-accent': accent }}
                      data-testid={`chip-${chip.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {Icon && <Icon size={13} className="shrink-0 transition-colors duration-200"
                        style={{ color: isS ? accent : isR ? `${accent}80` : 'rgba(var(--ce-text-rgb),0.3)' }} />}
                      <span className="text-[11px] sm:text-[12px] font-medium leading-tight transition-colors"
                        style={{ color: isS ? '#e2e8f0' : isR ? 'rgba(var(--ce-text-rgb),0.6)' : 'rgba(var(--ce-text-rgb),0.5)' }}>{chip.label}</span>
                    </motion.button>
                  );
                })}
              </div>
              {rememberedChip && selectedChipIndex === null && (
                <button onClick={clearChipMemory} className="text-[10px] mt-2 transition-colors" style={{ color: 'rgba(var(--ce-text-rgb),0.2)' }} data-testid="clear-chip-memory">Clear saved selection</button>
              )}
            </motion.div>
            <p className="text-[13px] mb-3" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }} data-testid="engine-or-describe">Or describe your own</p>
            <div className="mb-3">
              <div className="ce-input-container" style={{ borderColor: listening ? `${C.system}50` : programText ? 'rgba(var(--ce-text-rgb),0.1)' : 'rgba(var(--ce-text-rgb),0.06)', boxShadow: listening ? `0 0 20px ${C.system}15` : 'none' }} data-testid="input-container">
                <textarea ref={textareaRef} value={programText}
                  onChange={handleTextChange}
                  className="w-full px-4 py-3 text-[14px] leading-relaxed bg-transparent border-none outline-none resize-none"
                  style={{ color: 'var(--text-primary, #e2e8f0)', minHeight: '64px' }} placeholder={ROTATING_PLACEHOLDERS[placeholderIdx]} data-testid="program-input" />
                <div className="flex items-center justify-between px-4 pb-2.5">
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.12)' }} />

                  {hasVoice && (
                    <button onClick={toggleVoice} className="ce-voice-btn"
                      style={{ background: listening ? `${C.system}1A` : 'rgba(var(--ce-text-rgb),0.03)', borderColor: listening ? `${C.system}40` : 'rgba(var(--ce-text-rgb),0.08)', color: listening ? C.intelligence : 'rgba(var(--ce-text-rgb),0.4)' }}
                      data-testid="voice-btn">
                      <Mic size={13} />
                      <span className="text-[10px] font-medium" data-testid="engine-or-say">{listening ? 'Listening...' : 'Or just say it'}</span>
                    </button>
                  )}
                </div>
              </div>
              {voiceError && <p className="text-[11px] mt-1.5" style={{ color: '#EFD6B8' }} data-testid="voice-error">{voiceError}</p>}
            </div>
            <AnimatePresence>
              {nudge && (
                <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}
                  className="flex items-center gap-2 px-3 py-2 rounded-md mb-3"
                  style={{ background: `${C.system}08`, border: `1px solid ${C.system}15` }} data-testid="friction-nudge">
                  <div className="w-1 h-1 rounded-full" style={{ background: C.intelligence }} />
                  <span className="text-[12px] font-medium" style={{ color: `${C.intelligence}CC` }}>{nudge}</span>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Mobile deploy removed */}
          </div>

          <div>
            <div className="ce-control-panel" data-testid="control-model"
              style={{
                borderColor: confidence > 0.5 ? `${C.allowed}${Math.round(confidence * 0.12 * 255).toString(16).padStart(2, '0')}` : undefined,
                boxShadow: confidence > 0.6 ? `0 0 ${Math.round(confidence * 24)}px ${C.allowed}${Math.round(confidence * 0.06 * 255).toString(16).padStart(2, '0')}` : undefined,
              }}>
              <AnimatePresence mode="wait">
                {parsedProgram && constructionPhase > 0 ? (
                  <motion.div key="model" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-2">
                        <Lock size={12} style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }} />
                        <span className="text-[10px] font-mono uppercase tracking-[0.12em]" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }}>Control Model</span>
                      </div>
                      <span className="text-[10px] font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.2)' }} data-testid="preview-id">{previewId}</span>
                    </div>
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
                      className="flex items-center justify-between mb-5">
                      <h3 className="text-[16px] font-semibold" style={{ color: 'var(--text-primary, #e2e8f0)' }} data-testid="program-type">{parsedProgram.type}</h3>
                      <span className="text-[10px] font-mono font-semibold uppercase tracking-wider px-2 py-1 rounded"
                        style={{ color: hasConditions ? C.blocked : C.allowed, background: hasConditions ? `${C.blocked}12` : `${C.allowed}12`, border: `1px solid ${hasConditions ? `${C.blocked}25` : `${C.allowed}25`}` }}
                        data-testid="program-state">{hasConditions ? 'BLOCKED' : 'ELIGIBLE'}</span>
                    </motion.div>
                    <div className="space-y-3 mb-6">
                      {constructionPhase >= 1 && <CField label="WHO" value={inferBeneficiary(parsedProgram.type)} tid="field-who" d={0} />}
                      {constructionPhase >= 2 && <CField label="AMOUNT" value={parsedProgram.amount} tid="field-amount" d={0.05} />}
                      {constructionPhase >= 3 && <CField label="WHERE" value={parsedProgram.allowedUsage.join(', ')} tid="field-where" d={0.1} />}
                      {constructionPhase >= 4 && <CField label="WHEN" value={parsedProgram.timeline || 'No expiry defined'} tid="field-when" d={0.15} />}
                      {constructionPhase >= 5 && <CField label="CONDITIONS" value={parsedProgram.conditions.length > 0 ? parsedProgram.conditions.join(', ') : 'Ready on creation'} tid="field-conditions" d={0.2} />}
                    </div>

                    {/* Structural Confidence Meter */}
                    {constructionPhase >= 5 && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                        className="mb-4 flex items-center gap-2" data-testid="structural-confidence">
                        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(var(--ce-text-rgb),0.05)' }}>
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: `linear-gradient(90deg, ${C.system}, ${C.allowed})` }}
                            initial={{ width: '0%' }}
                            animate={{ width: `${Math.round(confidence * 100)}%` }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                          />
                        </div>
                        <span className="text-[9px] font-mono" style={{ color: confidence > 0.7 ? `${C.allowed}90` : `${C.system}70` }}>
                          {Math.round(confidence * 100)}% structured
                        </span>
                      </motion.div>
                    )}
                    {constructionPhase >= 6 && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                        className="pt-4" style={{ borderTop: '1px solid rgba(var(--ce-text-rgb),0.04)' }}>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-[10px] font-mono uppercase tracking-[0.12em]" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }} data-testid="sim-label">Test this program</p>
                          {totalTests > 0 && !simResult && (
                            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                              className="text-[10px] font-mono" style={{ color: `${C.system}50` }} data-testid="try-break-it">
                              Evaluate against rules.
                            </motion.span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="merchant-tests">
                          {merchantTests.map((t) => (
                            <button key={t.name} onClick={() => handleSimulate(t)}
                              disabled={simulating || resultLocked}
                              className="ce-merchant-btn" data-testid={`merchant-${t.name.toLowerCase().replace(/\s+/g, '-')}`}>
                              {t.name} &middot; ${t.amount.toLocaleString()}
                            </button>
                          ))}
                        </div>

                        {/* Previous action trace — system memory */}
                        {executionLog.length > 0 && !simulating && !simResult && (
                          <div className="mb-3 space-y-0.5" data-testid="action-trace">
                            {executionLog.slice(0, 3).map((log, li) => (
                              <motion.div key={log.timestamp} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                className="flex items-center gap-2" style={{ opacity: 0.25 - li * 0.06 }}>
                                {log.allowed ? <Check size={8} style={{ color: C.allowed }} /> : <X size={8} style={{ color: C.blocked }} />}
                                <span className="text-[9px] font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.3)' }}>
                                  {log.merchant} &middot; ${log.amount}
                                </span>
                              </motion.div>
                            ))}
                          </div>
                        )}

                        <AnimatePresence>
                          {simChecks.length > 0 && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                              className="space-y-1.5 mb-3 overflow-hidden">
                              {simulating && simVisibleChecks === 0 && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 1.2, repeat: Infinity }}
                                  className="flex items-center gap-2 py-1">
                                  <div className="w-1 h-1 rounded-full" style={{ background: C.pending }} />
                                  <span className="text-[11px] font-mono" style={{ color: `${C.pending}80` }}>Checking conditions...</span>
                                </motion.div>
                              )}
                              {simChecks.map((check, i) => (
                                i < simVisibleChecks && (
                                  <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}
                                    className="flex items-center gap-2 py-0.5" data-testid={`sim-check-${i}`}>
                                    {check.pass ? <Check size={11} style={{ color: C.allowed }} /> : <X size={11} style={{ color: C.blocked }} />}
                                    <span className="text-[11px] font-mono" style={{ color: check.pass ? `${C.allowed}CC` : `${C.blocked}CC` }}>{check.text}</span>
                                  </motion.div>
                                )
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Result with decision finality */}
                        <AnimatePresence>
                          {simResult && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0 }}
                              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                              data-testid="sim-result"
                              style={{ position: 'relative' }}
                            >
                              {/* Dim overlay during lock-in */}
                              {resultLocked && (
                                <motion.div
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  className="absolute -inset-3 rounded-xl pointer-events-none"
                                  style={{ background: 'rgba(0,0,0,0.15)', zIndex: -1 }}
                                />
                              )}
                              <div className="py-3 px-3.5 rounded-lg"
                                style={{
                                  background: simResult.allowed ? `${C.allowed}08` : `${C.blocked}08`,
                                  border: `1px solid ${simResult.allowed ? `${C.allowed}20` : `${C.blocked}20`}`,
                                  boxShadow: simResult.allowed ? `0 0 20px ${C.allowed}15` : `0 0 20px ${C.blocked}15`,
                                }}>
                                <div className="flex items-center gap-2">
                                  {simResult.allowed ? <Check size={13} style={{ color: C.allowed }} /> : <X size={13} style={{ color: C.blocked }} />}
                                  <span className="text-[12px] font-semibold" style={{ color: simResult.allowed ? C.allowed : C.blocked }}>
                                    {simResult.allowed ? 'ALLOWED' : 'BLOCKED'}
                                  </span>
                                  <span className="text-[11px]" style={{ color: 'rgba(var(--ce-text-rgb),0.4)' }}>
                                    {simResult.allowed ? `\u2014 ${simResult.merchant} verified` : `\u2014 ${simResult.reason}`}
                                  </span>
                                </div>

                                {/* Real engine indicator */}
                                {simResult.real && (
                                  <div className="flex items-center gap-2 mt-1.5" data-testid="real-evaluation-badge">
                                    <div className="w-1 h-1 rounded-full" style={{ background: C.system }} />
                                    <span className="text-[9px] font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.2)' }}>
                                      {simResult.evaluation_id} &middot; {simResult.latency_ms}ms &middot; proof:{simResult.proof_hash?.slice(0, 8)}
                                    </span>
                                  </div>
                                )}

                                {/* WHY explanation — deterministic, precise */}
                                <AnimatePresence>
                                  {assertionVisible && (
                                    <motion.div
                                      initial={{ opacity: 0, y: 4 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.4 }}
                                      className="mt-2 space-y-1"
                                      data-testid="why-explanation"
                                    >
                                      <p className="text-[10px] font-mono uppercase tracking-wider" style={{ color: simResult.allowed ? `${C.allowed}60` : `${C.blocked}60` }}>
                                        {simResult.allowed ? 'Why this passed' : 'What needs to change'}
                                      </p>
                                      {simResult.checks?.map((check, ci) => (
                                        <p key={ci} className="text-[10px] font-mono" style={{ color: check.pass ? `${C.allowed}80` : `${C.blocked}80` }}>
                                          {check.pass ? 'Verified' : 'Missing'}: {check.text}
                                        </p>
                                      ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>

                                {/* Break-the-system adaptive message */}
                                <AnimatePresence>
                                  {breakMessage && !simResult.allowed && assertionVisible && (
                                    <motion.p
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      transition={{ delay: 0.3, duration: 0.4 }}
                                      className="text-[10px] font-mono mt-1"
                                      style={{ color: `${C.blocked}50` }}
                                      data-testid="break-message"
                                    >
                                      {breakMessage}
                                    </motion.p>
                                  )}
                                </AnimatePresence>

                                {!simResult.allowed && !resultLocked && (
                                  <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
                                    onClick={() => { setSimResult(null); setSimChecks([]); setAssertionVisible(false); }}
                                    className="text-[10px] font-mono mt-2 transition-colors"
                                    style={{ color: `${C.blocked}60` }} data-testid="what-needs-to-change">
                                    Try another test
                                  </motion.button>
                                )}

                                {/* Share trigger — appears on BLOCKED results from real engine */}
                                {simResult.real && simResult.evaluation_id && !resultLocked && (
                                  <ShareTrigger evaluationId={simResult.evaluation_id} isBlocked={!simResult.allowed} />
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )}
                    {/* Deployment actions removed — system presence only */}

                    {/* Personal collision — appears after testing, feels like a thought */}
                    <AnimatePresence>
                      {totalTests >= 2 && !simulating && !resultLocked && (
                        <motion.p
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.2, duration: 0.6 }}
                          className="text-[11px] font-mono mt-4 text-center"
                          style={{ color: 'rgba(var(--ce-text-rgb),0.15)' }}
                          data-testid="personal-collision"
                        >
                          What would you want to control?
                        </motion.p>
                      )}
                    </AnimatePresence>
                    {deployPhase === 'error' && (
                      <p className="flex items-center gap-1.5 text-[11px] mt-3" style={{ color: '#EFD6B8' }} data-testid="deploy-error"><AlertCircle size={12} />{deployError}</p>
                    )}
                  </motion.div>
                ) : (
                  <AwakeningPanel intensity={intensity} systemStatus={systemStatus} onInject={injectProgram} />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <style>{engineStyles}</style>
    </section>
  );
}

/* Share Trigger — subtle, non-product prompt after BLOCKED evaluation */
function ShareTrigger({ evaluationId, isBlocked }) {
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await fetch(`${API}/api/execution/moment/${evaluationId}/share`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        const url = `${window.location.origin}/moment/${data.share_id}`;
        setShareUrl(url);
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* graceful */ }
    setSharing(false);
  };

  if (shareUrl) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2">
        <p className="text-[9px] font-mono" style={{ color: 'rgba(var(--ce-text-rgb),0.15)' }} data-testid="share-link-text">
          {copied ? 'Link copied' : 'Shareable link ready'}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.8 }}
      onClick={handleShare}
      disabled={sharing}
      className="text-[9px] font-mono mt-2 transition-colors"
      style={{ color: isBlocked ? 'rgba(215,210,234,0.3)' : 'rgba(199,227,212,0.3)', cursor: 'pointer', background: 'none', border: 'none' }}
      data-testid="share-moment-trigger"
    >
      {isBlocked ? 'This would have gone through. It didn\u0027t.' : 'This was evaluated and allowed.'}
    </motion.button>
  );
}


function CField({ label, value, tid, d = 0 }) {
  const fieldColors = { WHO: '#60A5FA', AMOUNT: '#34D399', WHERE: '#F59E0B', WHEN: '#A78BFA', CONDITIONS: '#F87171' };
  const accent = fieldColors[label] || '#B8D7F0';
  return (
    <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, delay: d }}
      className="flex items-start gap-3" data-testid={tid}>
      <span className="text-[9px] font-mono uppercase tracking-[0.1em] mt-1 w-[70px] shrink-0 px-1.5 py-0.5 rounded"
        style={{ color: accent, background: `${accent}10`, border: `1px solid ${accent}18` }}>{label}</span>
      <span className="text-[13px]" style={{ color: 'rgba(var(--ce-text-rgb),0.65)' }}>{value}</span>
    </motion.div>
  );
}

/* ═══ CHIP SEMANTIC ACCENT COLORS (ATTIO-GRADE) ═══ */
const CHIP_COLORS = {
  'Employee Benefits': '#22D3EE', /* soft cyan */
  'Vendor Payment': '#F59E0B',    /* soft amber */
  'Grant Funding': '#A78BFA',     /* violet */
  'CSR Disbursement': '#34D399',  /* emerald */
  'Startup Funding': '#60A5FA',   /* electric blue */
  'Climate Incentive': '#2DD4BF', /* green-teal */
  'Insurance Payout': '#F87171',  /* red-coral */
  'Government Subsidy': '#FB923C',/* orange */
  'Milestone Payment': '#C084FC', /* purple */
};

const engineStyles = `
  .ce-this-money { position: relative; display: inline-block; }
  .ce-this-money::after { content: ''; position: absolute; bottom: -1px; left: 0; width: 100%; height: 2px; background: #B8D7F0; border-radius: 1px; }
  .ce-chip { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 8px; text-align: left; cursor: pointer; background: rgba(var(--ce-text-rgb),0.02); border: 1px solid rgba(var(--ce-text-rgb),0.06); transition: all 200ms ease; }
  .ce-chip:hover { transform: translateY(-1px); border-color: var(--chip-accent, #B8D7F040); box-shadow: 0 4px 20px color-mix(in srgb, var(--chip-accent, #B8D7F0) 12%, transparent); background: rgba(var(--ce-text-rgb),0.04); }
  .ce-chip:hover svg { filter: brightness(1.3); }
  .ce-chip:active { transform: scale(0.97); transition: transform 100ms ease; }
  .ce-chip.selected { border-color: var(--chip-accent, #B8D7F04D); background: color-mix(in srgb, var(--chip-accent, #B8D7F0) 6%, transparent); box-shadow: 0 0 14px color-mix(in srgb, var(--chip-accent, #B8D7F0) 10%, transparent); }
  .ce-chip.selected svg { color: var(--chip-accent, #B8D7F0) !important; }
  .ce-chip.remembered { border-color: var(--chip-accent, #B8D7F01F); background: color-mix(in srgb, var(--chip-accent, #B8D7F0) 3%, transparent); }
  .ce-input-container { border-radius: 10px; background: rgba(var(--ce-text-rgb),0.02); border: 1px solid rgba(var(--ce-text-rgb),0.06); transition: all 200ms ease; overflow: hidden; }
  .ce-input-container:focus-within { border-color: #B8D7F033; box-shadow: 0 0 24px #B8D7F00F; }
  .ce-voice-btn { display: flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 7px; border: 1px solid; transition: all 150ms ease; cursor: pointer; }
  .ce-voice-btn:hover { border-color: #B8D7F033; color: #B8D7F0; }
  .ce-control-panel { border-radius: 16px; padding: 24px; background: rgba(var(--ce-text-rgb),0.01); border: 1px solid rgba(var(--ce-text-rgb),0.035); backdrop-filter: blur(8px); position: relative; min-height: 380px; transition: border-color 600ms ease, box-shadow 600ms ease; }
  .ce-control-panel::before { content: ''; position: absolute; inset: -1px; border-radius: 17px; padding: 1px; background: linear-gradient(135deg, #B8D7F018, #D7D2EA0A, transparent 60%); -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: xor; mask-composite: exclude; pointer-events: none; }
  .ce-merchant-btn { padding: 5px 10px; border-radius: 6px; font-size: 11px; font-family: 'JetBrains Mono', monospace; background: rgba(var(--ce-text-rgb),0.03); border: 1px solid rgba(var(--ce-text-rgb),0.06); color: rgba(var(--ce-text-rgb),0.45); cursor: pointer; transition: all 150ms ease; }
  .ce-merchant-btn:hover { border-color: rgba(var(--ce-text-rgb),0.15); color: rgba(var(--ce-text-rgb),0.7); background: rgba(var(--ce-text-rgb),0.05); }
  .ce-merchant-btn:disabled { opacity: 0.4; cursor: wait; }
  .ce-btn-primary { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 11px 24px; border-radius: 10px; font-size: 13px; font-weight: 600; background: rgba(var(--ce-text-rgb),0.04); color: var(--text-primary, #e2e8f0); border: 1px solid #B8D7F040; box-shadow: 0 0 20px #B8D7F014; transition: all 180ms ease; cursor: pointer; }
  .ce-btn-primary:hover { transform: translateY(-2px); border-color: #B8D7F066; box-shadow: 0 4px 28px #B8D7F026; background: #B8D7F00F; }
  .ce-btn-primary:active { transform: translateY(0) scale(0.97); }
  .ce-btn-primary:disabled { opacity: 0.35; cursor: not-allowed; transform: none; box-shadow: none; }
  .ce-btn-ghost { display: inline-flex; align-items: center; gap: 6px; padding: 11px 20px; border-radius: 10px; font-size: 12px; font-weight: 500; background: transparent; color: rgba(var(--ce-text-rgb),0.4); border: 1px solid rgba(var(--ce-text-rgb),0.06); transition: all 150ms ease; cursor: pointer; }
  .ce-btn-ghost:hover { border-color: rgba(var(--ce-text-rgb),0.15); color: rgba(var(--ce-text-rgb),0.6); }
  .awakening-bg { background-image: radial-gradient(circle at 1px 1px, #B8D7F014 1px, transparent 0); background-size: 32px 32px; }
`;
